import type { LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { createPortal } from 'react-dom';

import { formatNumber } from '@/shared/utils/number';

import { Money } from './Money';
import { StatusPill } from './StatusPill';

export interface SaleBillLine {
  id: string;
  name: string;
  quantity: number;
  unitPricePaise: number;
  lineTotalPaise: number;
  mrpPaise?: number;
}

export interface SaleBillRow {
  label: string;
  amountPaise: number;
  savings?: boolean;
}

export interface SaleBillPill {
  label: string;
  tone: 'neutral' | 'success' | 'warning' | 'danger' | 'info';
  icon: LucideIcon;
}

export interface SaleBillProps {
  brand: string;
  subtitle: string;
  storeLabel?: string;
  storeCity?: string;
  soldAtLabel?: string;
  orderNumber: string;
  orderId?: string;
  pills?: SaleBillPill[];
  customerLabel: string;
  customerName?: string;
  customerPhone?: string;
  customerAddressLines?: string[];
  emptyCustomerLabel: string;
  itemsLabel: string;
  formatLineMeta: (quantity: string) => string;
  lines: SaleBillLine[];
  rows: SaleBillRow[];
  payableLabel: string;
  payablePaise: number;
  paidLabel?: string;
  paidPaise?: number;
  note?: string;
  printCopy?: boolean;
}

export function SaleBillPrintCopy(props: Omit<SaleBillProps, 'printCopy'>) {
  return createPortal(<SaleBill {...props} printCopy />, document.body);
}

export function SaleBill({
  brand,
  subtitle,
  storeLabel,
  storeCity,
  soldAtLabel,
  orderNumber,
  orderId,
  pills = [],
  customerLabel,
  customerName,
  customerPhone,
  customerAddressLines = [],
  emptyCustomerLabel,
  itemsLabel,
  formatLineMeta,
  lines,
  rows,
  payableLabel,
  payablePaise,
  paidLabel,
  paidPaise,
  note,
  printCopy = false,
}: SaleBillProps) {
  const customer = customerName || customerPhone || customerAddressLines.length > 0;

  return (
    <article
      className={
        printCopy
          ? 'sale-bill-print'
          : 'mx-auto w-full max-w-sm rounded-md border border-dashed border-border bg-surface px-4 py-5'
      }
      aria-hidden={printCopy || undefined}
    >
      <header className="text-center">
        <p className="text-base font-semibold tracking-wide text-fg">{brand}</p>
        <p className="text-xs text-fg-muted">{subtitle}</p>
        {storeLabel ? <p className="mt-2 text-sm font-medium text-fg">{storeLabel}</p> : null}
        {storeCity ? <p className="text-xs text-fg-muted">{storeCity}</p> : null}
        {soldAtLabel ? <p className="mt-2 text-xs text-fg-muted">{soldAtLabel}</p> : null}
      </header>

      <p
        className="mt-4 text-center text-xl font-semibold tracking-tight text-fg"
        title={orderId || undefined}
      >
        {orderNumber || orderId}
      </p>
      {pills.length > 0 ? (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {pills.map((pill) => (
            <StatusPill key={pill.label} tone={pill.tone} icon={pill.icon}>
              {pill.label}
            </StatusPill>
          ))}
        </div>
      ) : null}

      <section className="mt-4 border-t border-dashed border-border pt-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-fg-muted">
          {customerLabel}
        </h3>
        {customer ? (
          <div className="mt-1 text-sm">
            {customerName ? <p className="font-medium text-fg">{customerName}</p> : null}
            {customerPhone ? <p className="text-fg-muted">{customerPhone}</p> : null}
            {customerAddressLines.map((line, index) => (
              <p key={`${index}-${line}`} className="text-fg-muted">
                {line}
              </p>
            ))}
          </div>
        ) : (
          <p className="mt-1 text-sm text-fg">{emptyCustomerLabel}</p>
        )}
      </section>

      <section className="mt-4 border-t border-dashed border-border pt-3">
        <h3 className="text-xs font-medium uppercase tracking-wide text-fg-muted">{itemsLabel}</h3>
        <ul className="mt-2 flex flex-col gap-3">
          {lines.map((line) => (
            <BillLine
              key={line.id}
              line={line}
              meta={formatLineMeta(formatNumber(line.quantity))}
            />
          ))}
        </ul>
      </section>

      <dl className="mt-4 flex flex-col gap-1.5 border-t border-dashed border-border pt-3 text-sm">
        {rows.map((row) => (
          <BillRow
            key={row.label}
            label={row.label}
            amount={row.amountPaise}
            savings={row.savings}
          />
        ))}
        <div className="mt-1 flex items-baseline justify-between gap-3 border-t border-border pt-2 text-base font-semibold text-fg">
          <dt>{payableLabel}</dt>
          <dd className="tabular-nums">
            <Money amount={payablePaise} />
          </dd>
        </div>
        {paidLabel !== undefined && paidPaise !== undefined ? (
          <div className="flex items-baseline justify-between gap-3 font-medium text-fg">
            <dt>{paidLabel}</dt>
            <dd className="tabular-nums">
              <Money amount={paidPaise} />
            </dd>
          </div>
        ) : null}
      </dl>
      {note ? <p className="mt-3 text-xs text-fg-muted">{note}</p> : null}
    </article>
  );
}

function BillLine({ line, meta }: { line: SaleBillLine; meta: ReactNode }) {
  return (
    <li className="flex items-start justify-between gap-3 text-sm">
      <div className="min-w-0">
        <p className="font-medium leading-5 text-fg">{line.name}</p>
        <p className="text-xs text-fg-muted">
          {meta} <Money amount={line.unitPricePaise} />
          {line.mrpPaise !== undefined && line.mrpPaise > line.unitPricePaise ? (
            <span className="ml-1 line-through">
              <Money amount={line.mrpPaise} />
            </span>
          ) : null}
        </p>
      </div>
      <p className="shrink-0 font-medium tabular-nums text-fg">
        <Money amount={line.lineTotalPaise} />
      </p>
    </li>
  );
}

function BillRow({
  label,
  amount,
  savings = false,
}: {
  label: string;
  amount: number;
  savings?: boolean;
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className="text-fg-muted">{label}</dt>
      <dd className={savings ? 'tabular-nums text-success-strong' : 'tabular-nums text-fg'}>
        <Money amount={amount} />
      </dd>
    </div>
  );
}
